export const COPILOT_FEATURES = {
  ACCESS: 'feature:app:ai:copilot:access',
  CHAT_USE: 'feature:app:ai:copilot:chat:use',
  CONVERSATION_ORGANIZE: 'feature:app:ai:copilot:conversation:organize',
  FILE_UPLOAD: 'feature:app:ai:copilot:chat:file-upload',
  INTEGRATION_VIEW: 'feature:app:ai:copilot:integration:view',
  INTEGRATION_OAUTH_MANAGE: 'feature:app:ai:copilot:integration:oauth:manage',
  KNOWLEDGE_USE: 'feature:app:ai:copilot:knowledge:use',
  AGENT_CHAT: 'feature:app:ai:copilot:agent:chat',
  AGENT_SEARCH_HISTORY: 'feature:app:ai:copilot:agent:search-history',
  AGENT_DRAFT_VIEW: 'feature:app:ai:copilot:agent:draft:view',
  AGENT_CREATE: 'feature:app:ai:copilot:agent:create',
  AGENT_EDIT: 'feature:app:ai:copilot:agent:edit',
  AGENT_DELETE: 'feature:app:ai:copilot:agent:delete',
  AGENT_LIFECYCLE: 'feature:app:ai:copilot:agent:lifecycle',
  FAQ_ADMIN: 'feature:app:ai:copilot:faq:admin',
  WIKI_BROWSE: 'feature:app:ai:wiki:browse',
  CONNECTOR_SYSTEM_VIEW: 'feature:app:ai:connector:system:view',
} as const;

export const COPILOT_SCREENS = {
  CHAT_USE: 'copilot:/chat',
  FAQ_USER: 'copilot:/chat#faquser',
  FAQ_ADMIN: 'copilot:/chat#faqadmin',
  CONVERSATION_RENAME: 'copilot:/chat#conversation-rename',
  CONVERSATION_DELETE: 'copilot:/chat#conversation-delete',
  CONVERSATION_PIN: 'copilot:/chat#conversation-pin',
  CONVERSATION_UNPIN: 'copilot:/chat#conversation-unpin',
  FILE_UPLOAD: 'copilot:/chat#file-upload',
  INTEGRATION_VIEW: 'copilot:/chat#connector-view',
  INTEGRATION_CONNECT: 'copilot:/chat#connector-connect',
  INTEGRATION_DISCONNECT: 'copilot:/chat#connector-disconnect',
  KNOWLEDGE_USE: 'copilot:/chat#knowledge-use',
  AGENT_PUBLISHED_VIEW: 'copilot:/chat/agents#published',
  AGENT_SELECTOR_VIEW: 'copilot:/chat#agent-selector',
  AGENT_DRAFT_VIEW: 'copilot:/chat/agents#draft',
  AGENT_CREATE: 'copilot:/chat/agents#create',
  AGENT_EDIT: 'copilot:/chat/agents#edit',
  AGENT_DELETE: 'copilot:/chat/agents#delete',
  AGENT_PUBLISH: 'copilot:/chat/agents#publish',
  AGENT_UNPUBLISH: 'copilot:/chat/agents#unpublish',
  AGENT_RESOURCE_SELECT: 'copilot:/chat/agents#resource-select',
  AGENT_AVATAR_UPLOAD: 'copilot:/chat/agents#avatar-upload',
  AGENT_ASSIST: 'copilot:/chat/agents#assist',
  AGENT_SEARCH_HISTORY: 'copilot:/chat/agents#search-history',
  WIKI_HOME: 'wiki:/',
  CONNECTOR_HOME: 'connector:/',
} as const;

export type EntitlementsStatus = 'idle' | 'loading' | 'loaded' | 'error';

export interface WorkspaceEntitlements {
  workspaceId: string | null;
  status: EntitlementsStatus;
  screens: string[];
  features: string[];
}

export interface CopilotAccess {
  isReady: boolean;
  canAccessApp: boolean;
  canUseChat: boolean;
  canViewUserFaq: boolean;
  canViewAdminFaq: boolean;
  canRenameConversation: boolean;
  canDeleteConversation: boolean;
  canPinConversation: boolean;
  canUnpinConversation: boolean;
  canUploadFiles: boolean;
  canViewIntegrations: boolean;
  canConnectIntegration: boolean;
  canDisconnectIntegration: boolean;
  canUseKnowledge: boolean;
  canUseAgentChat: boolean;
  canViewPublishedAgents: boolean;
  canViewDraftAgents: boolean;
  canSearchAgentHistory: boolean;
  canCreateAgent: boolean;
  canEditAgent: boolean;
  canDeleteAgent: boolean;
  canPublishAgent: boolean;
  canUnpublishAgent: boolean;
  canSelectAgentResources: boolean;
  canUploadAgentAvatar: boolean;
  canUseAgentAssist: boolean;
  canOpenWikiApp: boolean;
  canOpenConnectorApp: boolean;
}

const DENIED_ACCESS: CopilotAccess = {
  isReady: false,
  canAccessApp: false,
  canUseChat: false,
  canViewUserFaq: false,
  canViewAdminFaq: false,
  canRenameConversation: false,
  canDeleteConversation: false,
  canPinConversation: false,
  canUnpinConversation: false,
  canUploadFiles: false,
  canViewIntegrations: false,
  canConnectIntegration: false,
  canDisconnectIntegration: false,
  canUseKnowledge: false,
  canUseAgentChat: false,
  canViewPublishedAgents: false,
  canViewDraftAgents: false,
  canSearchAgentHistory: false,
  canCreateAgent: false,
  canEditAgent: false,
  canDeleteAgent: false,
  canPublishAgent: false,
  canUnpublishAgent: false,
  canSelectAgentResources: false,
  canUploadAgentAvatar: false,
  canUseAgentAssist: false,
  canOpenWikiApp: false,
  canOpenConnectorApp: false,
};

function normalizeCodes(values: string[] | null | undefined): Set<string> {
  return new Set(
    (values ?? [])
      .map(value => value?.trim().toLowerCase())
      .filter((value): value is string => Boolean(value))
  );
}

export function getCopilotAccess(
  entitlements: WorkspaceEntitlements,
  selectedWorkspaceId: string | null | undefined
): CopilotAccess {
  const isReady =
    entitlements.status === 'loaded' &&
    Boolean(selectedWorkspaceId) &&
    entitlements.workspaceId === selectedWorkspaceId;

  if (!isReady) return DENIED_ACCESS;

  const screens = normalizeCodes(entitlements.screens);
  const features = normalizeCodes(entitlements.features);
  const hasScreen = (screen: string) => screens.has(screen);
  const hasFeature = (feature: string) => features.has(feature);
  const hasAnyFeature = (...featureCodes: string[]) => featureCodes.some(hasFeature);
  const hasCapability = (feature: string, screen: string) =>
    hasFeature(feature) && hasScreen(screen);

  const canUseChat = hasCapability(COPILOT_FEATURES.CHAT_USE, COPILOT_SCREENS.CHAT_USE);
  const canViewIntegrations =
    hasScreen(COPILOT_SCREENS.INTEGRATION_VIEW) &&
    hasAnyFeature(COPILOT_FEATURES.INTEGRATION_VIEW, COPILOT_FEATURES.INTEGRATION_OAUTH_MANAGE);
  const canViewPublishedAgents =
    hasScreen(COPILOT_SCREENS.AGENT_PUBLISHED_VIEW) &&
    hasAnyFeature(
      COPILOT_FEATURES.AGENT_CHAT,
      COPILOT_FEATURES.AGENT_SEARCH_HISTORY,
      COPILOT_FEATURES.AGENT_EDIT,
      COPILOT_FEATURES.AGENT_DELETE,
      COPILOT_FEATURES.AGENT_LIFECYCLE
    );
  const canViewDraftAgents =
    hasScreen(COPILOT_SCREENS.AGENT_DRAFT_VIEW) &&
    hasAnyFeature(
      COPILOT_FEATURES.AGENT_DRAFT_VIEW,
      COPILOT_FEATURES.AGENT_CREATE,
      COPILOT_FEATURES.AGENT_EDIT,
      COPILOT_FEATURES.AGENT_DELETE,
      COPILOT_FEATURES.AGENT_LIFECYCLE
    );
  const canCreateAgent = hasCapability(COPILOT_FEATURES.AGENT_CREATE, COPILOT_SCREENS.AGENT_CREATE);
  const canEditAgent = hasCapability(COPILOT_FEATURES.AGENT_EDIT, COPILOT_SCREENS.AGENT_EDIT);
  const canPublishAgent = hasCapability(
    COPILOT_FEATURES.AGENT_LIFECYCLE,
    COPILOT_SCREENS.AGENT_PUBLISH
  );
  const canUnpublishAgent = hasCapability(
    COPILOT_FEATURES.AGENT_LIFECYCLE,
    COPILOT_SCREENS.AGENT_UNPUBLISH
  );
  const hasBuilderFeature = hasAnyFeature(
    COPILOT_FEATURES.AGENT_CREATE,
    COPILOT_FEATURES.AGENT_EDIT
  );

  return {
    isReady: true,
    canAccessApp: hasFeature(COPILOT_FEATURES.ACCESS),
    canUseChat,
    canViewUserFaq: canUseChat && hasScreen(COPILOT_SCREENS.FAQ_USER),
    canViewAdminFaq: hasCapability(COPILOT_FEATURES.FAQ_ADMIN, COPILOT_SCREENS.FAQ_ADMIN),
    canRenameConversation: hasCapability(
      COPILOT_FEATURES.CONVERSATION_ORGANIZE,
      COPILOT_SCREENS.CONVERSATION_RENAME
    ),
    canDeleteConversation: hasCapability(
      COPILOT_FEATURES.CONVERSATION_ORGANIZE,
      COPILOT_SCREENS.CONVERSATION_DELETE
    ),
    canPinConversation: hasCapability(
      COPILOT_FEATURES.CONVERSATION_ORGANIZE,
      COPILOT_SCREENS.CONVERSATION_PIN
    ),
    canUnpinConversation: hasCapability(
      COPILOT_FEATURES.CONVERSATION_ORGANIZE,
      COPILOT_SCREENS.CONVERSATION_UNPIN
    ),
    canUploadFiles: hasCapability(COPILOT_FEATURES.FILE_UPLOAD, COPILOT_SCREENS.FILE_UPLOAD),
    canViewIntegrations,
    canConnectIntegration:
      canViewIntegrations &&
      hasCapability(COPILOT_FEATURES.INTEGRATION_OAUTH_MANAGE, COPILOT_SCREENS.INTEGRATION_CONNECT),
    canDisconnectIntegration:
      canViewIntegrations &&
      hasCapability(
        COPILOT_FEATURES.INTEGRATION_OAUTH_MANAGE,
        COPILOT_SCREENS.INTEGRATION_DISCONNECT
      ),
    canUseKnowledge: hasCapability(COPILOT_FEATURES.KNOWLEDGE_USE, COPILOT_SCREENS.KNOWLEDGE_USE),
    canUseAgentChat:
      hasFeature(COPILOT_FEATURES.AGENT_CHAT) &&
      hasScreen(COPILOT_SCREENS.AGENT_PUBLISHED_VIEW) &&
      hasScreen(COPILOT_SCREENS.AGENT_SELECTOR_VIEW),
    canViewPublishedAgents,
    canViewDraftAgents,
    canSearchAgentHistory:
      canViewPublishedAgents &&
      hasCapability(COPILOT_FEATURES.AGENT_SEARCH_HISTORY, COPILOT_SCREENS.AGENT_SEARCH_HISTORY),
    canCreateAgent,
    canEditAgent,
    canDeleteAgent: hasCapability(COPILOT_FEATURES.AGENT_DELETE, COPILOT_SCREENS.AGENT_DELETE),
    canPublishAgent,
    canUnpublishAgent,
    canSelectAgentResources: hasBuilderFeature && hasScreen(COPILOT_SCREENS.AGENT_RESOURCE_SELECT),
    canUploadAgentAvatar: hasBuilderFeature && hasScreen(COPILOT_SCREENS.AGENT_AVATAR_UPLOAD),
    canUseAgentAssist: hasBuilderFeature && hasScreen(COPILOT_SCREENS.AGENT_ASSIST),
    canOpenWikiApp: hasCapability(COPILOT_FEATURES.WIKI_BROWSE, COPILOT_SCREENS.WIKI_HOME),
    canOpenConnectorApp: hasCapability(
      COPILOT_FEATURES.CONNECTOR_SYSTEM_VIEW,
      COPILOT_SCREENS.CONNECTOR_HOME
    ),
  };
}
